import { Check } from "lucide-react";
import { useState } from "react";
import { cn } from "../../../lib/utils";
import { lpConfig, type PricingPlan } from "../lpConfig";
import { useFadeIn } from "../useFadeIn";

const ACCENT_COLORS: Record<
  string,
  { border: string; bg: string; text: string; btnBg: string; btnHover: string }
> = {
  green: {
    border: "border-emerald-400",
    bg: "bg-emerald-500",
    text: "text-emerald-600",
    btnBg: "bg-emerald-500",
    btnHover: "hover:bg-emerald-600",
  },
  blue: {
    border: "border-blue-400",
    bg: "bg-blue-500",
    text: "text-blue-600",
    btnBg: "bg-gray-100",
    btnHover: "hover:bg-gray-200",
  },
  orange: {
    border: "border-[#ff8d00]",
    bg: "bg-[#ff8d00]",
    text: "text-[#ff8d00]",
    btnBg: "bg-[#ff8d00]",
    btnHover: "hover:opacity-90",
  },
  purple: {
    border: "border-purple-400",
    bg: "bg-purple-500",
    text: "text-purple-600",
    btnBg: "bg-gray-100",
    btnHover: "hover:bg-gray-200",
  },
  navy: {
    border: "border-[#0F1649]",
    bg: "bg-[#0F1649]",
    text: "text-[#0F1649]",
    btnBg: "bg-gray-100",
    btnHover: "hover:bg-gray-200",
  },
};

/**
 * 数値を日本円のカンマ区切りフォーマットに変換する
 * @param {number} value - フォーマット対象の数値
 * @return {string} カンマ区切りの円フォーマット文字列
 */
function formatYen(value: number): string {
  return `\u00A5${Math.round(value).toLocaleString()}`;
}

/**
 * プランに応じたCTAボタンのラベルを返す
 * @param {PricingPlan} plan - 料金プラン
 * @return {string} CTAボタンラベル
 */
function getCtaLabel(plan: PricingPlan): string {
  if (plan.monthlyPrice === 0) return lpConfig.pricing.ctaFree;
  if (plan.monthlyPrice === null) return lpConfig.pricing.ctaWithoutPrice;
  return lpConfig.pricing.ctaWithPrice;
}

/**
 * 料金プランセクション
 * 月額・年額切替トグルと5つの料金プランカードを表示する
 * 行動経済学に基づいた損失回避メッセージ、1人あたり単価、乗換バッジを含む
 * @return {JSX.Element} 料金セクションコンポーネント
 */
export function LpPricing() {
  const { ref, isVisible } = useFadeIn();
  const [isAnnual, setIsAnnual] = useState(false);

  const bestPerUser = Math.min(
    ...lpConfig.pricing.plans
      .map(p => p.perUserPrice)
      .filter((v): v is number => v !== null && v > 0),
  );

  return (
    <section
      id="pricing"
      className="py-24 lg:py-32 bg-gradient-to-b from-gray-50 to-white"
    >
      <div
        ref={ref}
        className={cn(
          "max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 lp-fade-in-up",
          isVisible && "lp-visible",
        )}
      >
        <div className="text-center">
          <p className="text-xs font-bold tracking-widest text-[#1667CF] uppercase mb-4">
            {lpConfig.pricing.sectionLabel}
          </p>
          <h2 className="text-4xl font-bold text-[#0F1649] mb-4">
            {lpConfig.pricing.heading}
          </h2>
          <p className="text-gray-500 mb-4">{lpConfig.pricing.subtitle}</p>

          {/* Loss Aversion Message */}
          <p className="text-red-600 font-bold text-sm mb-8">
            {lpConfig.pricing.lossAversionMessage}
          </p>

          {/* Toggle */}
          <div className="inline-flex bg-gray-100 rounded-full p-1">
            <button
              type="button"
              className={cn(
                "px-4 py-2 rounded-full text-sm transition-all",
                !isAnnual
                  ? "bg-white shadow-sm font-bold text-[#0F1649]"
                  : "text-gray-500",
              )}
              onClick={() => setIsAnnual(false)}
            >
              {lpConfig.pricing.monthlyLabel}
            </button>
            <button
              type="button"
              className={cn(
                "px-4 py-2 rounded-full text-sm transition-all",
                isAnnual
                  ? "bg-white shadow-sm font-bold text-[#0F1649]"
                  : "text-gray-500",
              )}
              onClick={() => setIsAnnual(true)}
            >
              {lpConfig.pricing.annualLabel}
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6 mt-12">
          {lpConfig.pricing.plans.map(plan => {
            const accent =
              ACCENT_COLORS[plan.accentColor] ?? ACCENT_COLORS.blue;

            const displayPrice =
              plan.monthlyPrice !== null
                ? isAnnual
                  ? Math.round(
                      plan.monthlyPrice * (1 - lpConfig.pricing.annualDiscount),
                    )
                  : plan.monthlyPrice
                : null;

            const annualSavings =
              plan.monthlyPrice !== null && plan.monthlyPrice > 0
                ? Math.round(
                    plan.monthlyPrice * lpConfig.pricing.annualDiscount * 12,
                  )
                : 0;

            const perUserAnnual =
              plan.perUserPrice !== null && isAnnual
                ? Math.round(
                    plan.perUserPrice * (1 - lpConfig.pricing.annualDiscount),
                  )
                : plan.perUserPrice;

            const isBestValue =
              plan.perUserPrice !== null && plan.perUserPrice === bestPerUser;

            // CTA button styling: accent button for free/popular plans, gray for others
            const useAccentButton = plan.monthlyPrice === 0 || plan.isPopular;
            const btnClasses = useAccentButton
              ? `${accent.btnBg} ${accent.btnHover}`
              : "bg-gray-100 hover:bg-gray-200";
            const btnTextClasses = useAccentButton
              ? "text-white"
              : "text-[#0F1649]";

            return (
              <div
                key={plan.name}
                className={cn(
                  "rounded-2xl border relative flex flex-col overflow-hidden",
                  plan.isPopular
                    ? `border-2 ${accent.border} shadow-xl xl:scale-105`
                    : "border-gray-200",
                )}
              >
                {/* Accent Bar */}
                <div className={cn("h-1 w-full", accent.bg)} />

                <div className="p-6 flex flex-col flex-1">
                  {plan.isPopular && (
                    <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 inline-block px-3 py-1 text-xs font-bold rounded-b-lg bg-[#ff8d00] text-white">
                      {lpConfig.pricing.popularBadge}
                    </span>
                  )}

                  <h3 className={cn("text-lg font-bold mb-1", accent.text)}>
                    {plan.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    {plan.description}
                  </p>

                  {/* Price */}
                  <div className="mb-1">
                    {displayPrice !== null ? (
                      <p>
                        <span className="text-3xl font-bold text-[#02072F]">
                          {formatYen(displayPrice)}
                        </span>
                        <span className="text-sm text-gray-400 font-normal">
                          {lpConfig.pricing.priceSuffix}
                        </span>
                      </p>
                    ) : (
                      <p className="text-3xl font-bold text-[#02072F]">
                        {plan.priceLabel}
                      </p>
                    )}
                  </div>

                  {/* Per-user price */}
                  {perUserAnnual !== null && perUserAnnual > 0 && (
                    <div className="mb-1">
                      <span className="text-xs text-gray-500">
                        {lpConfig.pricing.perUserLabel}{" "}
                        {formatYen(perUserAnnual)}
                        {lpConfig.pricing.priceSuffix}
                      </span>
                      {isBestValue && (
                        <span className="ml-1 inline-block px-1.5 py-0.5 text-[10px] font-bold rounded bg-emerald-100 text-emerald-700">
                          {lpConfig.pricing.perUserBestValue}
                        </span>
                      )}
                    </div>
                  )}

                  {/* User limit */}
                  <p className="text-xs text-gray-400 mb-4">{plan.userLimit}</p>

                  {/* Annual savings */}
                  {isAnnual && annualSavings > 0 && (
                    <p className="text-xs font-bold text-emerald-600 mb-4">
                      {lpConfig.pricing.annualSavingsPrefix}
                      {formatYen(annualSavings)}
                      {lpConfig.pricing.annualSavingsSuffix}
                    </p>
                  )}

                  {/* Features */}
                  <ul className="space-y-2 mb-6 flex-1">
                    {plan.features.map(feature => (
                      <li key={feature} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#30BF78] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <button
                    type="button"
                    className={cn(
                      "w-full py-3 rounded-xl font-bold text-center transition-colors",
                      btnClasses,
                      btnTextClasses,
                    )}
                  >
                    {getCtaLabel(plan)}
                  </button>

                  {/* Switch Badge */}
                  {plan.showSwitchBadge && (
                    <p className="mt-3 text-center text-[10px] text-[#ff8d00] font-bold leading-tight">
                      {lpConfig.pricing.switchBadge}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
